import React from "react";
import { Phone, Sparkles, ShieldCheck, Star, Clock } from "lucide-react";
import { HOME } from "../constants/testIds";
import { useSiteConfig } from "../lib/siteConfig";
import { openWhatsApp, callPhone, defaultWhatsAppMessage } from "../lib/whatsapp";
import { WhatsAppIcon } from "./Header";
import BookingForm from "./BookingForm";
import ResponsiveImage from "./ui/ResponsiveImage";

const HERO_IMAGE = "/media/fleet-20261019-v2/fleet-hero-front.webp";

export default function Hero() {
    const config = useSiteConfig();

    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-brand-navy">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy to-slate-800" />
                <div className="absolute inset-0 grid-lines opacity-10" />
            </div>

            <div className="container-x relative py-7 md:py-9 lg:py-10">
                <div className="grid gap-7 lg:grid-cols-12 lg:items-start">
                    <div className="min-w-0 lg:col-span-6 animate-fade-up">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold backdrop-blur-md">
                            <Sparkles className="h-3.5 w-3.5" />
                            Local & Interstate Travel
                        </div>

                        <h1 className="mt-4 font-display text-3xl font-extrabold leading-[1.12] sm:text-4xl xl:text-5xl">
                            <span style={{ color: "#FFFFFF", display: "block" }}>
                                Taxi & Tempo Traveller Services{" "}
                            </span>
                            <span style={{ color: "#F5A623", display: "block" }}>
                                Across Multiple States
                            </span>
                        </h1>

                        <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                            Book AC cabs, SUVs and Tempo Travellers with drivers for local rides, outstation journeys, airport and railway transfers, and group travel across our listed service locations.
                        </p>

                        <p className="mt-3 text-sm font-semibold text-brand-gold">Corporate Office: Satna, Madhya Pradesh</p>

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                data-testid={HOME.heroBookWhatsapp}
                                onClick={() =>
                                    openWhatsApp(
                                        config.whatsapp,
                                        defaultWhatsAppMessage(),
                                        "hero",
                                    )
                                }
                                className="btn-whatsapp"
                            >
                                <WhatsAppIcon className="h-4 w-4" />
                                Enquire on WhatsApp
                            </button>
                            <button
                                type="button"
                                data-testid={HOME.heroCallNow}
                                onClick={() => callPhone(config.phone, "hero")}
                                className="btn-gold"
                            >
                                <Phone className="h-4 w-4" />
                                Call Now · {config.phone_display}
                            </button>
                        </div>

                        <figure className="mt-6 overflow-hidden rounded-2xl border border-white/20 shadow-soft" data-testid="hero-fleet-photo">
                            <ResponsiveImage
                                src={HERO_IMAGE}
                                alt="VVPSS taxi fleet outside the office, with sedans and SUVs ready for hire"
                                priority width={1448} height={1086}
                                sizes="(max-width: 1024px) 100vw, 600px"
                                className="h-44 w-full object-cover object-center sm:h-56 lg:h-60"
                            />
                        </figure>

                        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs">
                            <Trust icon={<Clock className="h-4 w-4" />} label="24 × 7 Available" />
                            <Trust icon={<ShieldCheck className="h-4 w-4" />} label="GST Invoice" />
                            <Trust icon={<Star className="h-4 w-4" />} label="Trained Drivers" />
                            <Trust icon={<Sparkles className="h-4 w-4" />} label="Sanitised Vehicles" />
                        </div>
                    </div>

                    <div className="min-w-0 lg:col-span-6 animate-fade-up" style={{ animationDelay: "150ms" }}>
                        <BookingForm
                            variant="dark"
                            sourcePage="hero-form"
                            dense
                            heading="Request Your Trip Quote"
                            subheading="Check vehicle availability and get your fare on WhatsApp."
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Trust({ icon, label }) {
    return (
        <div className="inline-flex items-center gap-1.5 text-white/85">
            <span className="text-brand-gold">{icon}</span>
            <span className="font-semibold">{label}</span>
        </div>
    );
}
